/**
 * Utilidad compartida de formateo de fechas para el Portal del Socio.
 *
 * `formatearFechaLarga` produce SIEMPRE el formato legible en español:
 *   "9 de octubre de 2026"
 * (mes en minúscula, sin ceros iniciales, con "de" antes del año).
 *
 * Acepta y normaliza:
 *   - ISO date/datetime:        "2026-10-09" / "2026-10-09T20:00:00"
 *   - Legible abreviado:        "31 Dic 2099"  → "31 de diciembre de 2099"
 *   - Legible con coma:         "9 de octubre, 2026" → "9 de octubre de 2026"
 *   - Legible ya canónico:      "9 de octubre de 2026" (sin cambios)
 *   - Vacío / no string:        cadena vacía.
 *   - Formato irreconocible:    se devuelve tal cual.
 *
 * No altera valores almacenados ni enviados al backend: es sólo presentación.
 */

const MESES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

// Clave = primeras 3 letras (sin acentos, minúsculas). Cubre nombres
// completos y abreviaturas ("dic" ⊂ "diciembre", "sep"/"set" ⊂ "septiembre").
const MES_INDICE = {
  ene: 0,
  feb: 1,
  mar: 2,
  abr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  ago: 7,
  sep: 8,
  set: 8,
  oct: 9,
  nov: 10,
  dic: 11,
};

const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})/;

const sinAcentos = (s) =>
  s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const componer = (dia, mesIdx, anio) =>
  `${dia} de ${MESES[mesIdx]} de ${anio}`;

export const formatearFechaLarga = (valor) => {
  if (!valor || typeof valor !== "string") return "";
  const t = valor.trim();
  if (!t) return "";

  // Caso 1 · ISO (con o sin hora).
  const m = ISO_RE.exec(t);
  if (m) {
    const anio = Number(m[1]);
    const mes = Number(m[2]);
    const dia = Number(m[3]);
    if (mes >= 1 && mes <= 12 && dia >= 1 && dia <= 31) {
      return componer(dia, mes - 1, anio);
    }
  }

  // Caso 2 · Legible en español (abreviado, con coma o completo).
  // Extrae día, mes (por nombre) y año de 4 dígitos.
  const norm = sinAcentos(t);
  const tokens = norm.split(/[^a-z0-9]+/).filter(Boolean);
  let dia = null;
  let anio = null;
  let mesIdx = null;
  for (const tok of tokens) {
    if (/^\d{1,2}$/.test(tok) && dia === null) {
      dia = Number(tok);
      continue;
    }
    if (/^\d{4}$/.test(tok) && anio === null) {
      anio = Number(tok);
      continue;
    }
    if (mesIdx === null && /^[a-z]{3,}$/.test(tok)) {
      const clave = tok.slice(0, 3);
      if (clave in MES_INDICE) mesIdx = MES_INDICE[clave];
    }
  }
  if (dia !== null && mesIdx !== null && anio !== null && dia >= 1 && dia <= 31) {
    return componer(dia, mesIdx, anio);
  }

  // Caso 3 · Formato desconocido → sin cambios.
  return t;
};

export default formatearFechaLarga;
